import React, { useState } from 'react'
import { TimelineEvent } from 'react-event-timeline'

import { Box, Flex, Icon, Link, Text, theme } from '@driftt/dds'
import { Avatar } from '@driftt/tide-core'

import { ActivityContext, ActivityType, activityTypeToString } from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'

const BubbleStyles: Record<string, Record<string, any>> = {
  [ActivityType.PAGE_VIEW]: {
    borderColor: theme.colors.white,
    backgroundColor: '#24b232'
  },
  [ActivityType.CONVERSATION]: {
    borderColor: theme.colors.white,
    backgroundColor: '#5196ed'
  },
  [ActivityType.VIDEO]: {
    borderColor: theme.colors.white,
    backgroundColor: '#d8863d'
  },
  [ActivityType.MEETING_BOOKED]: {
    borderColor: theme.colors.white,
    backgroundColor: '#f05b4d'
  },
  [ActivityType.EMAIL_SEND]: {
    borderColor: theme.colors.white,
    backgroundColor: '#b987d9'
  }
}

const InsightCard = ({ icon, date, type, numActivities, activityList }: any) => {
  const [showMore, setShowMore] = useState<boolean>(false)
  const activityType = activityTypeToString(type) || type
  const activitiesByEndUserId =
    activityList?.reduce((acc: Record<number, any>, currentVal: any) => {
      const { endUserId } = currentVal
      const endUserData = acc[endUserId] ?? []
      return {
        ...acc,
        [endUserId]: [...endUserData, currentVal]
      }
    }, {}) || {}

  const numEndUsers = Object.keys(activitiesByEndUserId).length
  const activitiesListByUserId =
    Object.entries(activitiesByEndUserId)?.map(([id, activities]: any[]) => ({
      user: {
        email: activities[0]?.email,
        id
      },
      activities
    })) || []

  const { known, unknown } = activitiesListByUserId.reduce(
    (acc: any, { user }) => {
      const update = (type: string, increment: boolean) =>
        (acc[type] = acc[type] + increment ? 1 : 0)
      const isKnown = !!user.email
      return {
        known: update('known', isKnown),
        unknown: update('unknown', !isKnown)
      }
    },
    { known: 0, unknown: 0 }
  )

  const renderMetaData = (activityType: ActivityType, activityContext: ActivityContext) => {
    const {
      pageTitle,
      pageUrl,
      conversationPreview,
      id,
      title,
      url,
      percentViewed,
      meetingType
    } = activityContext

    switch (activityType) {
      case ActivityType.PAGE_VIEW:
        return (
          <Box>
            <Text truncate variant="metadata">
              viewed{' '}
              <Link href={pageUrl} external target="_blank" type="subtle" size="small">
                &quot;{pageTitle}&quot;
              </Link>
            </Text>
          </Box>
        )
      case ActivityType.CONVERSATION:
        return (
          <Flex>
            <Box>
              <Text variant="metadata">
                said{' '}
                <Link href={`/conversations/${id}`} type="subtle" size="small">
                  &quot;{conversationPreview}&quot;
                </Link>
              </Text>
            </Box>
          </Flex>
        )
      case ActivityType.MEETING_BOOKED:
        return (
          <Flex>
            <Box>
              <Text variant="metadata">booked a &quot;{meetingType}&quot; meeting</Text>
            </Box>
          </Flex>
        )
      case ActivityType.VIDEO:
        return (
          <Flex>
            <Box>
              <Text variant="metadata">
                viewed {percentViewed}% of{' '}
                <Link href={url} type="subtle" external target="_blank" size="small">
                  &quot;{title}&quot;
                </Link>
              </Text>
            </Box>
          </Flex>
        )
      default:
        return ''
    }
  }
  return (
    <TimelineEvent
      titleStyle={{
        fontFamily: theme.fonts.primary,
        fontSize: theme.fontSizes[2]
      }}
      bubbleStyle={BubbleStyles[type]}
      icon={<Icon icon={icon} color={theme.colors.white.toString()} />}
      subtitle={date}
      title={`${numActivities} ${pluralize(activityType, numActivities)} ${
        numEndUsers > 1 ? `from ${numEndUsers} people` : ''
      }`.trim()}>
      <Flex direction="column" css={{ width: '100%' }}>
        <Flex alignItems="center" css={{ width: '100%' }} justifyContent="space-between">
          <Flex alignItems="flex-start" direction="column">
            {!showMore ? (
              <Flex>
                {activitiesListByUserId?.map(({ user }: any = {}, idx: number) => {
                  if (!user) {
                    return null
                  }
                  return (
                    <Box
                      key={user.id}
                      css={{
                        transform: idx > 0 ? 'translateX(-80%)' : 'none',
                        width: 'fit-content'
                      }}>
                      <Avatar user={user} />
                    </Box>
                  )
                })}
              </Flex>
            ) : (
              <div />
            )}
            {!showMore && (
              <>
                <Flex>
                  {known > 0 ? (
                    <Text as="span">{`${known} known ${pluralize('user', known)}`}</Text>
                  ) : null}

                  {unknown > 0 ? (
                    <Text as="span">{`${unknown} unknown ${pluralize('user', unknown)}`}</Text>
                  ) : null}
                </Flex>
              </>
            )}
          </Flex>
          <Link
            css={{ align: 'flex-end' }}
            type="primary"
            onClick={() => setShowMore(!showMore)}>{`${
            showMore ? 'Hide' : 'View'
          } activity details`}</Link>
        </Flex>

        {showMore && (
          <Box>
            {activitiesListByUserId.length &&
              activitiesListByUserId.map((userActivities: any) => {
                const { user, activities } = userActivities || {}
                if (!user || !activities) {
                  return null
                }
                return (
                  <Flex direction="column" gap={1} key={user.id}>
                    <Flex alignItems="center">
                      <Avatar user={user} />
                      {user.email ? (
                        <Link type="primary" href={`/activity/contacts/${user.id}`}>
                          {user.email}
                        </Link>
                      ) : (
                        <Text>Site Visitor</Text>
                      )}
                    </Flex>
                    <Box css={{ marginBottom: '$1' }}>
                      {activities.length &&
                        activities.map(
                          (activity: {
                            id: number
                            activityContext: ActivityContext
                            activityType: ActivityType
                          }) => {
                            const { id, activityContext, activityType } = activity
                            return (
                              <Flex key={id} direction="column" gap="half">
                                {renderMetaData(activityType, activityContext)}
                              </Flex>
                            )
                          }
                        )}
                    </Box>
                  </Flex>
                )
              })}
          </Box>
        )}
      </Flex>
    </TimelineEvent>
  )
}

export default InsightCard
